import { useEffect, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { renovarSesion, urlLogin } from '@/lib/sesion';
import { classNames } from '@/lib/utils';

const LOGO = 'https://serenodocsstor.blob.core.windows.net/assets/sereno-neg.png';

/** Por aquí avisa client/public/sesion-renovada.html de que se ha vuelto a entrar. */
const CANAL = 'crm-sesion';

type Props =
  | { motivo: 'entrar' }
  | { motivo: 'caducada' }
  | { motivo: 'sin-conexion'; detalle: string | null };

function entrarEnEstaPestana() {
  window.location.href = urlLogin(window.location.pathname + window.location.search);
}

/**
 * La pantalla de login: al abrir el CRM sin sesión, y encima de todo cuando la
 * sesión caduca con el CRM abierto.
 *
 * En el segundo caso Microsoft se abre en una ventana aparte en vez de en esta
 * pestaña. Así no se pierde lo que haya a medio escribir ni se corta una
 * llamada: el CCP de la centralita vive en esta página y una recarga lo tira.
 */
export default function PantallaLogin(props: Props) {
  const { motivo } = props;
  const [esperando, setEsperando] = useState(false);
  const ventana = useRef<Window | null>(null);

  useEffect(() => {
    if (motivo !== 'caducada' || typeof BroadcastChannel === 'undefined') return;
    const canal = new BroadcastChannel(CANAL);
    canal.onmessage = () => {
      ventana.current?.close();
      // Si el servidor no contesta, la vigilancia de la sesión lo reintenta al
      // volver el foco a esta pestaña.
      renovarSesion().catch(() => {});
    };
    return () => canal.close();
  }, [motivo]);

  const volverAEntrar = () => {
    // Mismo nombre de ventana: un segundo clic reutiliza la que ya está abierta.
    const abierta = window.open(urlLogin('/sesion-renovada.html'), 'crm-login', 'popup,width=520,height=680');
    if (!abierta) {
      // Ventanas emergentes bloqueadas: mejor entrar aquí que quedarse fuera.
      entrarEnEstaPestana();
      return;
    }
    ventana.current = abierta;
    abierta.focus();
    setEsperando(true);
  };

  const textos = {
    entrar: {
      titulo: 'Entra en el CRM',
      texto: 'Con tu cuenta de Microsoft del trabajo.',
    },
    caducada: {
      titulo: 'Tu sesión ha caducado',
      texto: 'Vuelve a entrar para seguir. Se abrirá una ventana de Microsoft y, al acabar, seguirás en esta pantalla tal como la dejaste.',
    },
    'sin-conexion': {
      titulo: 'No se puede conectar con el CRM',
      texto: import.meta.env.DEV
        ? 'No responde el servidor de localhost:3001.'
        : 'Puede que se esté actualizando. Suele tardar menos de un minuto.',
    },
  }[motivo];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="pantalla-login-titulo"
      className={classNames(
        'fixed inset-0 z-[100] flex items-center justify-center px-4',
        motivo === 'caducada' ? 'bg-sereno-dark/95 backdrop-blur-sm' : 'bg-sereno-dark',
      )}
    >
      <div className="w-full max-w-sm text-center text-white">
        <img src={LOGO} alt="Sereno" className="h-12 mx-auto mb-10" />
        <h1 id="pantalla-login-titulo" className="text-xl font-semibold">{textos.titulo}</h1>
        <p className="mt-2 text-sm text-white/60 leading-relaxed">{textos.texto}</p>
        {props.motivo === 'sin-conexion' && props.detalle && (
          <p className="mt-2 text-xs text-white/40">{props.detalle}</p>
        )}

        {/* Colores a mano y no bg-sereno-blue / text-sereno-dark: en modo
            oscuro index.css los fuerza a un azul casi transparente y a gris
            claro, y esta pantalla es oscura en los dos modos. */}
        <button
          autoFocus
          onClick={
            motivo === 'sin-conexion' ? () => window.location.reload()
              : motivo === 'caducada' ? volverAEntrar
                : entrarEnEstaPestana
          }
          className="mt-8 w-full inline-flex items-center justify-center gap-2.5 rounded-lg bg-[#D1E0F0] text-[#0B1D2E] px-4 py-3 font-medium hover:bg-[#E6EEF7] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-2 focus-visible:ring-offset-sereno-dark"
        >
          {motivo === 'sin-conexion' ? <RefreshCw size={18} /> : <LogoMicrosoft />}
          {motivo === 'sin-conexion' ? 'Reintentar'
            : motivo === 'caducada' ? 'Volver a entrar' : 'Entrar con Microsoft'}
        </button>

        {esperando && (
          <p className="mt-4 text-xs text-white/50 leading-relaxed" aria-live="polite">
            Esperando a que termines en la ventana de Microsoft.{' '}
            ¿No la ves?{' '}
            <button onClick={entrarEnEstaPestana} className="underline hover:text-white">
              Entrar en esta pestaña
            </button>
            {' '}(se pierde lo que no hayas guardado).
          </p>
        )}
      </div>
    </div>
  );
}

function LogoMicrosoft() {
  return (
    <svg width="16" height="16" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="0" y="0" width="10" height="10" fill="#F25022" />
      <rect x="11" y="0" width="10" height="10" fill="#7FBA00" />
      <rect x="0" y="11" width="10" height="10" fill="#00A4EF" />
      <rect x="11" y="11" width="10" height="10" fill="#FFB900" />
    </svg>
  );
}
